/**
 * Equipment library types — mirror rest_server /v4/equipment responses
 * (routes/v4/equipment.js). Value sets mirror the CHECK constraints in
 * migrations 0315_equipment_library.sql and 0318_equipment_kind_fridge_freezer.sql.
 */

export const EQUIPMENT_KINDS = ['undercounter', 'upright-fridge', 'upright-freezer', 'fridge-freezer', 'two-door', 'ult', 'incubator', 'chest', 'dewar'] as const;
export type EquipmentKind = (typeof EQUIPMENT_KINDS)[number];

export const EQUIPMENT_KIND_LABELS: Record<EquipmentKind, string> = {
  undercounter: 'Undercounter',
  'upright-fridge': 'Upright fridge',
  'upright-freezer': 'Upright freezer',
  'fridge-freezer': 'Fridge-freezer',
  'two-door': 'Two-door',
  ult: 'ULT freezer',
  incubator: 'Incubator',
  chest: 'Chest freezer',
  dewar: 'Dewar',
};

export const VERIFIED_VALUES = ['yes', 'face + height', 'face', 'no'] as const;
export type Verified = (typeof VERIFIED_VALUES)[number];

export const VERIFIED_LABELS: Record<Verified, string> = {
  yes: 'Verified',
  'face + height': 'Face + height verified',
  face: 'Face verified',
  no: 'Not verified',
};

export const PORT_FACES = ['back', 'left', 'right', 'top', 'none'] as const;
export type PortFace = (typeof PORT_FACES)[number];

export const DOCUMENT_KINDS = ['manual', 'service manual', 'datasheet', 'brochure', 'document'] as const;
export type DocumentKind = (typeof DOCUMENT_KINDS)[number];

export type InstallGuideStatus = 'verified' | 'partly' | 'none';
export type ModelStatus = 'active' | 'archived';

export interface EquipmentModelRow {
  id: string;
  brand: string;
  model: string;
  /** Null for imported directory models until a curator sets it. */
  kind: EquipmentKind | null;
  label: string;
  status: ModelStatus;
  wMm: number | null;
  dMm: number | null;
  hMm: number | null;
  drawing: Record<string, unknown>;
  portFace: PortFace | null;
  portU: number | null;
  portV: number | null;
  portMm: number | null;
  portVerified: Verified;
  probeU: number | null;
  probeV: number | null;
  probeW: number | null;
  probeVerified: Verified;
  sensorU: number | null;
  sensorV: number | null;
  sensorW: number | null;
  sensorVerified: Verified;
  sensorSource: string | null;
  installNotes: string | null;
  source: string | null;
  nocodbCode: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedBy: string | null;
  updatedAt: string;
  sensorCount: number;
  documentCount: number;
  photoCount: number;
  thumbnailPhotoId: string | null;
  installGuideStatus: InstallGuideStatus;
}

export type EquipmentModelInput = Pick<EquipmentModelRow, 'brand' | 'model' | 'kind' | 'label'> &
  Partial<Pick<EquipmentModelRow,
    'wMm' | 'dMm' | 'hMm' | 'portFace' | 'portU' | 'portV' | 'portMm' | 'portVerified'
    | 'probeU' | 'probeV' | 'probeW' | 'probeVerified'
    | 'sensorU' | 'sensorV' | 'sensorW' | 'sensorVerified' | 'sensorSource' | 'installNotes' | 'source'>>;

export interface EquipmentAlias {
  id: string;
  brandNorm: string;
  modelNorm: string;
}

export interface EquipmentDocument {
  id: string;
  kind: DocumentKind;
  title: string;
  gcsPath: string | null;
  externalUrl: string | null;
  originalFilename: string | null;
  sizeBytes: number | null;
  pageHint: string | null;
  createdAt: string;
}

export interface EquipmentPhoto {
  id: string;
  kind: 'product' | 'install';
  caption: string | null;
  gcsPath: string;
  createdAt: string;
}

export interface EquipmentModelDetail {
  model: EquipmentModelRow;
  aliases: EquipmentAlias[];
  documents: EquipmentDocument[];
  photos: EquipmentPhoto[];
}

export interface EquipmentModelSensor {
  mongoSensorId: string;
  name: string | null;
  accountName: string | null;
  siteName: string | null;
  linked: boolean;
}

export type SubmissionStatus = 'pending' | 'approved' | 'merged' | 'rejected';
export type SubmissionPhotoRole = 'plate' | 'unit' | 'cable_entry' | 'probe';

export interface SubmissionCandidate {
  modelId: string;
  exact: boolean;
  label: string;
  sensorCount: number;
}

export interface SubmissionRow {
  id: string;
  clientRequestId: string;
  jobId: string;
  sensorMongoId: string;
  submittedBy: string;
  submittedAt: string;
  aiRead: Record<string, unknown> | null;
  brand: string;
  model: string;
  serialNumber: string | null;
  kind: EquipmentKind | null;
  status: SubmissionStatus;
  reviewedBy: string | null;
  reviewedAt: string | null;
  resultModelId: string | null;
  rejectReason: string | null;
  jobNumber: string | null;
  siteName: string | null;
  thumbnailPhotoId: string | null;
  /** Short-lived signed URL of the plate (or first) photo; submission photos have no public id route. */
  thumbnailUrl: string | null;
  candidates: SubmissionCandidate[];
}

export interface SubmissionPhoto {
  id: string;
  role: SubmissionPhotoRole;
  url: string;
}

export interface SubmissionDetail {
  submission: SubmissionRow;
  photos: SubmissionPhoto[];
  candidates: SubmissionCandidate[];
}

export interface ReviewPhotos {
  publishPhotoIds: string[];
  photosConfirmed: boolean;
}
